/**
 * Extraction-screen state (web-pose-extraction step 4): progress → review → tweak/reset.
 * Detected landmarks are kept immutably so RESET always returns to what the model saw.
 */
import { FrameLandmarks } from "./clientPose";

export type ExtractionPhase = "idle" | "extracting" | "review";

export type ExtractionState = {
  phase: ExtractionPhase;
  totalFrames: number;
  frames: FrameLandmarks[];          // working copy (tweakable)
  detectedFrames: FrameLandmarks[];  // immutable originals for RESET
  currentFrame: number;
  progress: number;                  // 0..1
  tweakedFrames: Set<number>;
};

export type ExtractionAction =
  | { type: "START"; totalFrames: number }
  | { type: "FRAME_DONE"; frame: FrameLandmarks }
  | { type: "COMPLETE" }
  | { type: "GOTO_FRAME"; index: number }
  | { type: "TWEAK"; frameIndex: number; landmarkIndex: number; x: number; y: number }
  | { type: "RESET_FRAME"; frameIndex: number };

export function initialExtractionState(): ExtractionState {
  return {
    phase: "idle",
    totalFrames: 0,
    frames: [],
    detectedFrames: [],
    currentFrame: 0,
    progress: 0,
    tweakedFrames: new Set(),
  };
}

const cloneFrame = (f: FrameLandmarks): FrameLandmarks => ({
  landmarks: f.landmarks.map((l) => ({ ...l })),
});

export function extractionReducer(s: ExtractionState, a: ExtractionAction): ExtractionState {
  switch (a.type) {
    case "START":
      return { ...initialExtractionState(), phase: "extracting", totalFrames: a.totalFrames };
    case "FRAME_DONE": {
      const frames = [...s.frames, cloneFrame(a.frame)];
      const detectedFrames = [...s.detectedFrames, cloneFrame(a.frame)];
      return {
        ...s,
        frames,
        detectedFrames,
        progress: s.totalFrames > 0 ? frames.length / s.totalFrames : 0,
      };
    }
    case "COMPLETE":
      return { ...s, phase: "review", progress: 1, currentFrame: 0 };
    case "GOTO_FRAME":
      if (a.index < 0 || a.index >= s.frames.length) return s;
      return { ...s, currentFrame: a.index };
    case "TWEAK": {
      if (a.frameIndex < 0 || a.frameIndex >= s.frames.length) return s;
      const frames = s.frames.map((f, i) => {
        if (i !== a.frameIndex) return f;
        return {
          landmarks: f.landmarks.map((l) =>
            l.index === a.landmarkIndex ? { ...l, x: a.x, y: a.y } : l
          ),
        };
      });
      const tweakedFrames = new Set(s.tweakedFrames);
      tweakedFrames.add(a.frameIndex);
      return { ...s, frames, tweakedFrames };
    }
    case "RESET_FRAME": {
      if (a.frameIndex < 0 || a.frameIndex >= s.frames.length) return s;
      const frames = s.frames.map((f, i) =>
        i === a.frameIndex ? cloneFrame(s.detectedFrames[a.frameIndex]) : f
      );
      const tweakedFrames = new Set(s.tweakedFrames);
      tweakedFrames.delete(a.frameIndex);
      return { ...s, frames, tweakedFrames };
    }
    default:
      return s;
  }
}
